import { lazy } from 'react';
import { Card, Grid, LinearProgress, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import DashboardIcon from 'src/assets/icons/Generator/DashboardIcon';
import PrimaryCollapse from 'src/components/Collapsible/Primary';
import CollapsibleTitle from 'src/components/Collapsible/components/CollapsibleTitle';
import DashboardCard from 'src/pages/Dashboard/components/Cards/Card';
import PreloaderCard from 'src/pages/Dashboard/components/Cards/PreloaderCard';
import { isPending } from 'src/utils/common';
import { WithStore } from 'src/utils/store';
import { ActivityDashboardStore } from './store';

const AllAssignedActivitiesIcon = lazy(
  () => import('src/assets/icons/Activities/Dashboard/AllAssignedActivities'),
);
const MyAssignedActivitiesIcon = lazy(
  () => import('src/assets/icons/Activities/Dashboard/MyAssignedActivities'),
);

const ActivityDashboardComponent = observer(
  ({ store }: WithStore<{}, ActivityDashboardStore>) => {
    const isLoading = isPending(store.dataReq);

    return (
      <PrimaryCollapse
        isOpen={store.open}
        customHeader={
          <CollapsibleTitle
            title="Dashboard"
            isOpen={store.open}
            titleIcon={<DashboardIcon sx={{ width: 40, height: 40 }} />}
            handlerClick={store.toggleOpen}></CollapsibleTitle>
        }>
        <Card>
          {isLoading && <LinearProgress />}
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            sx={{ margin: 'auto', paddingBottom: 2 }}>
            <CardsContainer item container xs={12} md={12}>
              {isLoading ? (
                <>
                  <StyledDashboardCardItem item xs={6}>
                    <ScalableCardWrapper>
                      <PreloaderCard sx={{ width: '100%' }} />
                    </ScalableCardWrapper>
                  </StyledDashboardCardItem>
                  <StyledDashboardCardItem item xs={6}>
                    <ScalableCardWrapper>
                      <PreloaderCard sx={{ width: '100%' }} />
                    </ScalableCardWrapper>
                  </StyledDashboardCardItem>
                </>
              ) : (
                <>
                  <StyledDashboardCardItem item xs={6}>
                    <ScalableCardWrapper>
                      <DashboardCard
                        CardsIcon={AllAssignedActivitiesIcon}
                        value={store.data?.assignedActivityCount}
                        cardTitle="All Assigned Activities"
                        cardColor="#4D44E4"
                      />
                    </ScalableCardWrapper>
                  </StyledDashboardCardItem>
                  <StyledDashboardCardItem item xs={6} >
                    <ScalableCardWrapper>
                      <DashboardCard
                        CardsIcon={MyAssignedActivitiesIcon}
                        value={store.data?.userAssignedActivityCount}
                        cardTitle="My Assigned Activities"
                        cardColor="#049669"
                      />
                    </ScalableCardWrapper>
                  </StyledDashboardCardItem>
                </>
              )}
            </CardsContainer>
          </Grid>
        </Card>
      </PrimaryCollapse>
    );
  },
);

export const createActivityDashboard = () => {
  const store = new ActivityDashboardStore();

  return {
    activityDashboardStore: store,
    ActivityDashboard: () => <ActivityDashboardComponent store={store} />,
  };
};

const CardsContainer = styled(Grid)({
  containerType: 'inline-size',
  containerName: 'dashboard-cards',
});

const BASE_CARD_WIDTH = 200;
const CONTAINER_BASE = 450;
const CONTAINER_THRESHOLD = 1200;

const StyledDashboardCardItem = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(0, 1),
  display: 'flex',
  justifyContent: 'center',

  '@container dashboard-cards (min-width: 700px)': {
    '&:first-of-type': {
      justifyContent: 'flex-end',
    },
    '&:last-of-type': {
      justifyContent: 'flex-start',
    },
  },

  '@container dashboard-cards (min-width: 800px)': {
    '&:first-of-type': {
      justifyContent: 'center',
      paddingRight: theme.spacing(2),
    },
    '&:last-of-type': {
      justifyContent: 'flex-start',
      paddingLeft: theme.spacing(2),
    },
  },

  '@container dashboard-cards (min-width: 1200px)': {
    '&:first-of-type': {
      justifyContent: 'center',
      paddingRight: theme.spacing(4),
    },
    '&:last-of-type': {
      justifyContent: 'flex-start',
      paddingLeft: theme.spacing(4),
    },
  },
}));

const ScalableCardWrapper = styled('div')({
  width: '100%',

  '@container dashboard-cards (min-width: 700px)': {
    width: `calc(${BASE_CARD_WIDTH}px + (50cqw - ${CONTAINER_BASE / 2}px) * 0.8)`,
    minWidth: `${BASE_CARD_WIDTH}px`,
    maxWidth: '100%',
  },

  '@container dashboard-cards (min-width: 1200px)': {
    width: `calc(387px + (50cqw - ${CONTAINER_THRESHOLD / 2}px) * 0.5)`,
    maxWidth: '100%',
  },
});
